import React, { useEffect, useId, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { BotaoPequeno, Hint } from "../profiles-tracking/components/edit-profile-modal/components/EditorChrome";
import {
  buscarConfirmacao,
  buscarWhatsappDaFicha,
  corrigirWhatsapp,
  deveMostrarAviso,
  formatarNumero,
  normalizarNumero,
  numeroAtualNoGoogle,
  registrarConfirmacao,
  type ConfirmacaoDoWhatsapp,
  type WhatsappNaFicha,
} from "../../utils/aviso-whatsapp";

const Cartao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #dadce0;
  color: #1f1f1f;
  scroll-margin-top: 16px;
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }
`;

const Selo = styled.span`
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef7e0;
  color: #8a5a00;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
`;

const MiniFicha = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #e8eaed;
  .nome {
    font-size: 14px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .aberto {
    color: #188038;
    font-weight: 500;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const Chip = styled.span<{ $destaque?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${({ $destaque }) => ($destaque ? "#1e8e3e" : "#dadce0")};
  background: ${({ $destaque }) => ($destaque ? "#e6f4ea" : "#fff")};
  color: ${({ $destaque }) => ($destaque ? "#137333" : "#1264c7")};
  .material-icons {
    font-size: 16px;
  }
`;

const Numero = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

const Linha = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Campo = styled.input`
  flex: 1 1 180px;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #dadce0;
  font-size: 15px;
`;

const Rotulo = styled.label`
  font-size: 13px;
  color: #3c4043;
`;

const LinkDeTeste = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #1264c7;
  text-decoration: none;
  .material-icons {
    font-size: 16px;
  }
`;

type Etapa = "convite" | "conferir" | "corrigir" | "obrigado";

type Props = {
  profileId: string;
  nomeDaEmpresa: string;
  podeCorrigir: boolean;
};

const mensagemDeErro = (erro: unknown, padrao: string) =>
  erro instanceof Error && erro.message ? erro.message : padrao;

/**
 * Home do cliente: o Google pôs um botão de WhatsApp na ficha e o cliente
 * confere se é o número dele ("Ele chama você?"). Some depois da resposta e
 * volta se o número no Google mudar.
 */
export const AvisoDoWhatsapp: React.FC<Props> = ({ profileId, nomeDaEmpresa, podeCorrigir }) => {
  const [whatsapp, setWhatsapp] = useState<WhatsappNaFicha | null>(null);
  const [confirmacao, setConfirmacao] = useState<ConfirmacaoDoWhatsapp | null>(null);
  const [etapa, setEtapa] = useState<Etapa>("convite");
  const [numeroNovo, setNumeroNovo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const idDoCampo = useId();

  useEffect(() => {
    let ativo = true;
    Promise.all([buscarWhatsappDaFicha(profileId), buscarConfirmacao(profileId)]).then(([ficha, ultima]) => {
      if (!ativo) return;
      setWhatsapp(ficha);
      setConfirmacao(ultima);
    });
    return () => {
      ativo = false;
    };
  }, [profileId]);

  if (etapa !== "obrigado" && !deveMostrarAviso(whatsapp, confirmacao)) return null;

  const numeroNoGoogle = numeroAtualNoGoogle(whatsapp) ?? "";
  const linkDeTeste = `https://wa.me/${numeroNoGoogle}`;

  const confirmar = async () => {
    setOcupado(true);
    try {
      await registrarConfirmacao(profileId, { resultado: "CONFIRMOU", numero: numeroNoGoogle, numeroNoGoogle });
      setEtapa("obrigado");
    } catch (erro) {
      toast.error(mensagemDeErro(erro, "Não foi possível registrar. Tente de novo."));
    } finally {
      setOcupado(false);
    }
  };

  const corrigir = async (evento: React.FormEvent) => {
    evento.preventDefault();
    const numero = normalizarNumero(numeroNovo);
    if (!numero) {
      toast.error("Digite o número com DDD. Ex.: 62 99999-9999");
      return;
    }
    setOcupado(true);
    try {
      const ficha = await corrigirWhatsapp(profileId, numero);
      await registrarConfirmacao(profileId, { resultado: "CORRIGIU", numero, numeroNoGoogle });
      if (ficha) setWhatsapp(ficha);
      setEtapa("obrigado");
    } catch (erro) {
      toast.error(mensagemDeErro(erro, "Não foi possível salvar o número. Tente de novo."));
    } finally {
      setOcupado(false);
    }
  };

  if (etapa === "obrigado") {
    return (
      <Cartao id="whatsapp" aria-live="polite">
        <h3>Obrigado! Resposta registrada.</h3>
        <Hint>Se você corrigiu o número, o Google pode levar algumas horas para mostrar o novo.</Hint>
      </Cartao>
    );
  }

  return (
    <Cartao id="whatsapp" aria-live="polite">
      <Selo>EM TESTE NO GOOGLE</Selo>
      <h3>O Google colocou um botão de WhatsApp na sua empresa.</h3>
      <MiniFicha aria-hidden="true">
        <div className="nome">
          {nomeDaEmpresa || "Sua empresa"} · <span className="aberto">Aberto</span>
        </div>
        <Chips>
          <Chip><span className="material-icons">call</span>Ligar</Chip>
          <Chip><span className="material-icons">directions</span>Rotas</Chip>
          <Chip $destaque><span className="material-icons">chat</span>WhatsApp</Chip>
        </Chips>
      </MiniFicha>

      {etapa === "convite" && (
        <Linha>
          <BotaoPequeno type="button" $variante="filled" onClick={() => setEtapa("conferir")}>
            Ele chama você?
          </BotaoPequeno>
        </Linha>
      )}

      {etapa !== "convite" && (
        <>
          <div>
            <Hint>Número no botão do Google</Hint>
            <Numero>{formatarNumero(numeroNoGoogle)}</Numero>
            <LinkDeTeste href={linkDeTeste} target="_blank" rel="noreferrer">
              <span className="material-icons" aria-hidden="true">open_in_new</span>
              Testar: abrir no WhatsApp
            </LinkDeTeste>
          </div>
          {etapa === "conferir" && (
            <Linha>
              <BotaoPequeno type="button" $variante="filled" onClick={confirmar} disabled={ocupado}>
                Sim, é o meu número
              </BotaoPequeno>
              <BotaoPequeno type="button" $variante="outlined" onClick={() => setEtapa("corrigir")} disabled={ocupado}>
                Não, corrigir
              </BotaoPequeno>
            </Linha>
          )}
          {etapa === "corrigir" && podeCorrigir && (
            <form onSubmit={corrigir}>
              <Rotulo htmlFor={idDoCampo}>Qual é o WhatsApp certo da empresa?</Rotulo>
              <Linha style={{ marginTop: 6 }}>
                <Campo
                  id={idDoCampo}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="62 99999-9999"
                  value={numeroNovo}
                  onChange={(evento) => setNumeroNovo(evento.target.value)}
                />
                <BotaoPequeno type="submit" $variante="filled" disabled={ocupado}>Salvar</BotaoPequeno>
                <BotaoPequeno type="button" $variante="text" onClick={() => setEtapa("conferir")} disabled={ocupado}>
                  Voltar
                </BotaoPequeno>
              </Linha>
            </form>
          )}
          {etapa === "corrigir" && !podeCorrigir && (
            <Hint>Fale com a nossa equipe para trocar o número: seu acesso a esta empresa é só de leitura.</Hint>
          )}
        </>
      )}
    </Cartao>
  );
};
